import {AuthCookie} from "@buildwithflux/constants";
import {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {FluxClaims} from "@buildwithflux/models";
import {Logger} from "@buildwithflux/shared";
import {onIdTokenChanged} from "firebase/auth";
import {create} from "zustand";

import {CurrentUserService} from "modules/auth/types";

interface FluxClaimsStore {
    claims: FluxClaims;
    token: string | null;
    isLoading: boolean;
    error: string | null;
}

export class FluxAuthClaimsService {
    private store = create<FluxClaimsStore>(() => ({
        claims: {},
        token: null,
        isLoading: false,
        error: null,
    }));

    private unsubscribeAuth?: () => void;
    private unsubscribeTokenChanges?: () => void;
    private unsubscribeUserChanges?: () => void;
    private initialized = false;

    constructor(
        private readonly functionsAdapter: ClientFunctionsAdapter,
        private readonly currentUserService: CurrentUserService,
        private readonly logger: Logger,
    ) {}

    initialize(): void {
        if (this.initialized) return;
        this.initialized = true;

        this.logger.debug("FluxAuthClaimsService initializing...");

        this.unsubscribeAuth = this.currentUserService.subscribeToFirebaseUserChanges((user) => {
            this.logger.debug("FluxAuthClaimsService received Firebase user change", {user: !!user});
            if (user) {
                this.refreshFluxToken();
            } else {
                this.clearFluxToken();
            }
        });

        this.unsubscribeTokenChanges = onIdTokenChanged(this.currentUserService.firebaseAuth, (user) => {
            this.logger.debug("FluxAuthClaimsService received Firebase token change", {user: !!user});
            if (user) {
                this.refreshFluxToken();
            }
        });

        this.unsubscribeUserChanges = this.currentUserService.subscribeToUserChanges((userData) => {
            this.logger.debug("FluxAuthClaimsService received user change", {user: !!userData.user});
            if (userData.user) {
                this.refreshFluxToken();
            }
        });

        this.logger.debug("FluxAuthClaimsService initialized successfully");
    }

    private async refreshFluxToken(): Promise<void> {
        try {
            this.logger.debug("FluxAuthClaimsService calling fluxAuth function...");
            this.store.setState({isLoading: true, error: null});

            const result = await this.functionsAdapter.fluxAuth({});
            const data = result.data;

            this.logger.debug("FluxAuth function response received", {
                success: data.success,
                hasToken: !!data.fluxToken,
                hasClaims: !!data.claims,
            });

            if (data.success && data.fluxToken && data.claims) {
                this.setFluxTokenCookie(data.fluxToken);
                this.store.setState({
                    claims: data.claims,
                    token: data.fluxToken,
                    isLoading: false,
                    error: null,
                });
                this.logger.debug("Flux claims refreshed successfully", {claims: data.claims});
            } else {
                throw new Error(data.error || "Failed to refresh flux claims");
            }
        } catch (error) {
            this.logger.error("Failed to refresh flux claims", {error});
            this.store.setState({
                claims: {},
                token: null,
                isLoading: false,
                error: error instanceof Error ? error.message : "Unknown error",
            });
        }
    }

    private clearFluxToken(): void {
        document.cookie = `${AuthCookie}=; Max-Age=0; Path=/; Domain=.flux.ai; Secure; SameSite=Strict`;
        this.store.setState({
            claims: {},
            token: null,
            isLoading: false,
            error: null,
        });
        this.logger.debug("Flux claims cleared and reset");
    }

    private setFluxTokenCookie(token: string): void {
        document.cookie = `${AuthCookie}=${token}; Path=/; Domain=.flux.ai; Secure; SameSite=Strict`;
    }

    public getFluxToken(): string | null {
        return this.store.getState().token;
    }

    public subscribeToFluxToken(callback: (token: string | null) => void): () => void {
        const unsubscribe = this.store.subscribe((state, prevState) => {
            if (state.token !== prevState.token) {
                callback(state.token);
            }
        });

        callback(this.store.getState().token);

        return unsubscribe;
    }

    createFluxClaimsHook() {
        return this.store;
    }

    dispose(): void {
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
        }
        if (this.unsubscribeTokenChanges) {
            this.unsubscribeTokenChanges();
        }
        if (this.unsubscribeUserChanges) {
            this.unsubscribeUserChanges();
        }
    }

    shutdown(): Promise<void> {
        this.dispose();
        return Promise.resolve();
    }
}
